import { useState } from "react";
import { alertKind, alertLevel, sortAlerts } from "../../../shared/alerts";
import type { WeatherAlert } from "../../../shared/models";
import { dismissTakeover } from "../../app/alertDirector";
import { useHousehold } from "../../app/useHousehold";
import { dataStateOf } from "../../data/queries";
import { useAlerts } from "../../data/weatherQueries";
import { Icon } from "../../ui/Icon";
import { Sheet } from "../../ui/Sheet";
import { EmptyState, StatusBadge } from "../../ui/StatusBadge";
import { relativeAge, useNow } from "../../ui/time";
import { NeedsLocation } from "../sky/common";
import type { WidgetDefinition, WidgetProps } from "../types";
import { WidgetHeader } from "../WidgetHeader";
import { KIND_ICON, toneOf, untilText } from "./alertUi";
import "./alerts.css";

type AlertsConfig = Record<string, never>;

const LEVEL_LABEL = { takeover: "Act now", warning: "Warning", watch: "Watch", advisory: "Advisory", statement: "Statement" } as const;

function Alerts({ size, fullscreen, openFullscreen }: WidgetProps<AlertsConfig>) {
  const { settings, timeZone } = useHousehold();
  useNow("minute");
  const q = useAlerts(true);
  const state = dataStateOf(q, 15 * 60_000);
  const [open, setOpen] = useState<WeatherAlert | null>(null);
  if (!settings.location) return <NeedsLocation icon="alert" title="Alerts need a home location" detail="Set a general location to get National Weather Service alerts." compact={size.variant === "compact"} />;
  const data = q.data;
  if (!data) return state === "loading" ? <div className="widget-loading" /> : <EmptyState icon="alert" title="Alerts unavailable" detail={q.error?.message} />;

  const alerts = sortAlerts(data.alerts);
  const [hero, ...rest] = alerts;
  const h24 = settings.clock24h;
  const anyTakeover = alerts.some((a) => alertLevel(a) === "takeover");

  return (
    <div className="alerts">
      <WidgetHeader
        title="Weather alerts"
        meta={`${settings.location.label || "Home"} · checked ${relativeAge(data.fetchedAt)}`}
        onTitle={fullscreen ? undefined : openFullscreen}
        actions={
          anyTakeover ? (
            <button className="btn btn-sm" onClick={dismissTakeover}>
              <Icon name="check" size="1rem" /> Got it
            </button>
          ) : undefined
        }
      />
      {!hero ? (
        <div className="alerts-clear">
          <span className="alerts-clear-icon">
            <Icon name="check" size="2.2rem" />
          </span>
          <strong>All clear</strong>
          <span className="muted">No National Weather Service alerts for home right now.</span>
        </div>
      ) : (
        <div className="alerts-list">
          <button className={`alert-hero tone-${toneOf(hero)}`} onClick={() => setOpen(hero)}>
            <span className="alert-hero-top">
              <Icon name={KIND_ICON[alertKind(hero.event)]} size="2rem" />
              <span className="alert-level">{LEVEL_LABEL[alertLevel(hero)]}</span>
              {untilText(hero, timeZone, h24) && <span className="alert-until">{untilText(hero, timeZone, h24)}</span>}
            </span>
            <span className="alert-event">{hero.event}</span>
            {hero.headline && <span className="alert-headline">{hero.headline}</span>}
            {hero.instruction && (
              <span className="alert-instruction">
                <b>What to do:</b> {firstSentences(hero.instruction, fullscreen ? 6 : 2)}
              </span>
            )}
          </button>
          {rest.map((a) => (
            <button key={a.id} className={`alert-row tone-${toneOf(a)}`} onClick={() => setOpen(a)}>
              <Icon name={KIND_ICON[alertKind(a.event)]} size="1.3rem" />
              <span className="alert-row-name">{a.event}</span>
              <span className="muted small">{untilText(a, timeZone, h24)}</span>
            </button>
          ))}
        </div>
      )}
      <StatusBadge state={state} updatedAt={q.dataUpdatedAt} />
      {open && (
        <Sheet title={open.event} onClose={() => setOpen(null)} size="lg">
          <div className="alert-detail">
            <p className="muted">
              {open.senderName}
              {untilText(open, timeZone, h24) ? ` · ${untilText(open, timeZone, h24)}` : ""}
            </p>
            {open.areaDesc && <p className="small muted">Areas: {open.areaDesc}</p>}
            {open.instruction && (
              <>
                <h4>What to do</h4>
                <p className="pre">{open.instruction}</p>
              </>
            )}
            <h4>Details</h4>
            <p className="pre">{open.description}</p>
            <p className="muted small">Source: National Weather Service.</p>
          </div>
        </Sheet>
      )}
    </div>
  );
}

function firstSentences(text: string, n: number): string {
  const flat = text.replace(/\s+/g, " ").trim();
  const parts = flat.match(/[^.!?]+[.!?]+/g) ?? [flat];
  return parts.slice(0, n).join(" ").trim();
}

export const alertsWidget: WidgetDefinition<AlertsConfig> = {
  type: "alerts",
  version: 1,
  name: "Weather alerts",
  description: "Active National Weather Service alerts for home, largest first, with what to do. Shows “All clear” when there are none.",
  icon: "alert",
  category: "weather",
  defaultSize: { w: 5, h: 6 },
  minSize: { w: 3, h: 3 },
  stackHeight: 380,
  defaults: {},
  settings: [],
  provider: "nws",
  capabilities: { requiresLocation: true },
  supportsFullscreen: true,
  Component: Alerts,
};
